import { SimpleLegalFooter } from "@/components/LegalFooter";
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Car,
  Bike,
  User,
  FileText,
  FileCheck,
  Check,
  Search,
  CheckCircle2,
  Upload,
  Phone,
  Mail,
  Camera,
  CreditCard,
  IdCard,
  Send,
  Clock,
  Calendar,
  Download,
  Zap,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { HeaderLogo } from "@/components/AnimatedLogo";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo JYVIS
const JYVIS_LOGO = "/logo.svg";

// Contact Info
const contactInfo = {
  phone: "01 48 20 98 27",
  email: "contact@jyvis-assurances.com",
  whatsapp: "https://wa.me/message/XE3IM2IGGU5VE1",
};

// Tarifs temporaires avec liens Stripe
const TARIFS = {
  auto: { 
    1: { prix: 166, stripeLink: "https://buy.stripe.com/14A9AVgAh7LDbVk8bC2wU0w" }, 
    2: { prix: 255, stripeLink: "https://buy.stripe.com/4gM3cx83L8PH1gGajK2wU0x" }
  },
  moto: { 
    1: { prix: 155, stripeLink: "https://buy.stripe.com/14A9AVgAh7LDbVk8bC2wU0w" }, 
    2: { prix: 230, stripeLink: "https://buy.stripe.com/4gM3cx83L8PH1gGajK2wU0x" }
  },
};

export default function AssuranceTemporaire() {
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    // Type et durée
    vehiculeType: "auto",
    duree: "1",
    
    // Informations personnelles
    nom: "",
    prenom: "",
    dateNaissance: "",
    lieuNaissance: "",
    departementNaissance: "",
    paysNaissance: "France",
    telephone: "",
    email: "",
    adresse: "",
    codePostal: "",
    ville: "",
    
    // Permis de conduire
    datePermis: "",
    
    // Véhicule
    plaque: "",
    carteGriseBarree: false,
    
    // Dates du contrat (calculées automatiquement)
    dateDebutContrat: "",
    heureDebutContrat: "",
    dateFinContrat: "",
  });
  
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [accepted, setAccepted] = useState(false);
  
  // Documents
  const [carteGrise, setCarteGrise] = useState(null);
  const [permisConduire, setPermisConduire] = useState(null);
  const [photoVehicule, setPhotoVehicule] = useState(null);
  const [releveInfo, setReleveInfo] = useState(null);

  // Calculer la date de fin en fonction de la durée
  const calculateEndDate = (startDate, months) => {
    if (!startDate) return "";
    const date = new Date(startDate);
    date.setMonth(date.getMonth() + parseInt(months));
    return date.toISOString().split('T')[0];
  };

  // Mettre à jour les dates quand la durée ou date de début change
  const updateContractDates = (startDate, duree) => {
    const endDate = calculateEndDate(startDate, duree);
    setForm(prev => ({
      ...prev,
      dateDebutContrat: startDate,
      dateFinContrat: endDate
    }));
  };

  // Recherche plaque SIV
  const lookupPlate = async () => {
    if (!form.plaque || form.plaque.length < 7) {
      toast.error("Veuillez entrer une plaque valide");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/vehicle/lookup/${form.plaque}`);
      setVehicleInfo(response.data);
      toast.success("Véhicule identifié !");
    } catch (error) {
      toast.error("Impossible d'identifier le véhicule");
      setVehicleInfo(null);
    } finally {
      setLoading(false);
    }
  };

  // Prix actuel
  const getCurrentPrice = () => {
    return TARIFS[form.vehiculeType]?.[form.duree]?.prix || 0;
  };

  // Enregistrer la souscription et ouvrir Stripe
  const handlePayment = async () => {
    if (!form.nom || !form.prenom || !form.telephone || !form.email) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    if (!form.dateNaissance || !form.lieuNaissance || !form.paysNaissance) {
      toast.error("Veuillez remplir les informations de naissance");
      return;
    }
    
    if (!form.datePermis) {
      toast.error("Veuillez indiquer la date d'obtention du permis");
      return;
    }
    
    if (!form.plaque || form.plaque.replace(/[^a-z0-9]/gi, "").length < 5) {
      toast.error("Veuillez indiquer la plaque d'immatriculation");
      return;
    }

    if (!form.dateDebutContrat || !form.heureDebutContrat) {
      toast.error("Veuillez indiquer la date et l'heure de début du contrat");
      return;
    }
    
    if (!carteGrise || !permisConduire || !photoVehicule) {
      toast.error("Veuillez joindre tous les documents requis");
      return;
    }
    
    if (!accepted) {
      toast.error("Veuillez accepter les conditions");
      return;
    }
    
    setLoading(true);
    try {
      // Calculer la date de fin
      const dateFinContrat = calculateEndDate(form.dateDebutContrat, form.duree);
      
      // Enregistrer la souscription en base
      const response = await axios.post(`${API}/temporaire/subscribe`, {
        vehiculeType: form.vehiculeType,
        duree: parseInt(form.duree),
        prix: getCurrentPrice(),
        nom: form.nom,
        prenom: form.prenom,
        dateNaissance: form.dateNaissance,
        lieuNaissance: form.lieuNaissance,
        departementNaissance: form.departementNaissance,
        paysNaissance: form.paysNaissance,
        telephone: form.telephone,
        email: form.email,
        adresse: form.adresse,
        codePostal: form.codePostal,
        ville: form.ville,
        datePermis: form.datePermis,
        plaque: form.plaque,
        carteGriseBarree: form.carteGriseBarree,
        dateDebutContrat: form.dateDebutContrat,
        heureDebutContrat: form.heureDebutContrat,
        dateFinContrat: dateFinContrat,
        vehicleInfo: vehicleInfo,
        status: "pending_payment"
      });
      
      setSubscriptionId(response.data.id);
      setSubmitted(true);
      await startCheckout(response.data.id);
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const startCheckout = async (id) => {
    try {
      const res = await axios.post(`${API}/payments/temporaire/checkout`, { subscription_id: id, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Paiement indisponible, appelez-nous au 01 48 20 98 27");
    }
  };

  // Confirmer le paiement manuellement
  const confirmPayment = async () => {
    if (subscriptionId) {
      try {
        await axios.patch(`${API}/temporaire/confirm/${subscriptionId}`);
        setPaymentConfirmed(true);
        toast.success("Merci ! Votre paiement est en cours de vérification, l'attestation suit par email.");
      } catch (error) {
        toast.error("Erreur lors de la confirmation");
      }
    } else {
      setPaymentConfirmed(true);
      toast.success("Merci ! Paiement signalé.");
    }
  };

  // Générer facture PDF professionnelle
  const generateInvoice = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const invoiceNumber = `FAC-${Date.now()}`;
    const currentDate = new Date().toLocaleDateString('fr-FR');
    
    // Couleurs
    const primaryColor = [16, 38, 146]; // #102692
    const accentColor = [249, 115, 22]; // #F97316
    
    // En-tête avec fond bleu
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 45, 'F');
    
    // Logo JYVIS (texte stylisé car on ne peut pas charger l'image externe facilement)
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.text("JYVIS", 20, 25);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("ASSURANCES", 20, 33);
    
    // Informations société à droite
    doc.setFontSize(9);
    doc.text("47 rue Jean Jaurès", pageWidth - 20, 18, { align: "right" });
    doc.text("93200 Saint-Denis", pageWidth - 20, 24, { align: "right" });
    doc.text("Tél: " + contactInfo.phone, pageWidth - 20, 30, { align: "right" });
    doc.text("contact@jyvis-assurances.com", pageWidth - 20, 36, { align: "right" });
    
    // Titre FACTURE
    doc.setTextColor(...primaryColor);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("FACTURE", 20, 60);
    
    // Numéro et date
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(`N° ${invoiceNumber}`, 20, 68);
    doc.text(`Date: ${currentDate}`, 20, 74);
    
    // Cadre client
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(120, 50, 75, 40, 3, 3, 'F');
    doc.setTextColor(...primaryColor);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("CLIENT", 125, 58);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(9);
    doc.text(`${form.prenom} ${form.nom}`, 125, 66);
    doc.text(form.adresse || "-", 125, 72);
    doc.text(`${form.codePostal} ${form.ville}`, 125, 78);
    doc.text(form.email, 125, 84);
    
    // Section Véhicule
    doc.setFillColor(...accentColor);
    doc.rect(20, 100, 4, 20, 'F');
    doc.setTextColor(...primaryColor);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("INFORMATIONS VÉHICULE", 28, 108);
    
    // Tableau véhicule
    const vehicleData = [
      ["Type", form.vehiculeType === "auto" ? "Automobile" : "Moto"],
      ["Immatriculation", form.plaque || "-"],
      ["Marque", vehicleInfo?.brand || "-"],
      ["Modèle", vehicleInfo?.model || "-"],
      ["Carte grise barrée", form.carteGriseBarree ? "Oui" : "Non"],
    ];
    
    doc.autoTable({
      startY: 112,
      head: [],
      body: vehicleData,
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50, textColor: [100, 100, 100] },
        1: { cellWidth: 80, textColor: [30, 30, 30] }
      },
      margin: { left: 28 }
    });
    
    // Section Conducteur
    const conducteurY = doc.lastAutoTable.finalY + 15;
    doc.setFillColor(...primaryColor);
    doc.rect(20, conducteurY, 4, 20, 'F');
    doc.setTextColor(...primaryColor);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("INFORMATIONS CONDUCTEUR", 28, conducteurY + 8);
    
    const conducteurData = [
      ["Né(e) le", form.dateNaissance ? new Date(form.dateNaissance).toLocaleDateString('fr-FR') : "-"],
      ["Lieu de naissance", `${form.lieuNaissance || "-"} (${form.departementNaissance || "-"})`],
      ["Pays de naissance", form.paysNaissance || "France"],
      ["Permis obtenu le", form.datePermis ? new Date(form.datePermis).toLocaleDateString('fr-FR') : "-"],
      ["Téléphone", form.telephone || "-"],
    ];
    
    doc.autoTable({
      startY: conducteurY + 12,
      head: [],
      body: conducteurData,
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50, textColor: [100, 100, 100] },
        1: { cellWidth: 80, textColor: [30, 30, 30] }
      },
      margin: { left: 28 }
    });
    
    // Section Contrat
    const contratY = doc.lastAutoTable.finalY + 15;
    doc.setFillColor(16, 185, 129); // vert
    doc.rect(20, contratY, 4, 20, 'F');
    doc.setTextColor(...primaryColor);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("DÉTAILS DU CONTRAT", 28, contratY + 8);
    
    const dateDebut = form.dateDebutContrat ? new Date(form.dateDebutContrat).toLocaleDateString('fr-FR') : "-";
    const dateFin = form.dateFinContrat ? new Date(form.dateFinContrat).toLocaleDateString('fr-FR') : calculateEndDate(form.dateDebutContrat, form.duree) ? new Date(calculateEndDate(form.dateDebutContrat, form.duree)).toLocaleDateString('fr-FR') : "-";
    
    const contratData = [
      ["Type d'assurance", "Assurance Temporaire " + (form.vehiculeType === "auto" ? "Auto" : "Moto")],
      ["Durée", `${form.duree} mois`],
      ["Date de début", `${dateDebut} à ${form.heureDebutContrat || "00:00"}`],
      ["Date de fin", dateFin],
    ];
    
    doc.autoTable({
      startY: contratY + 12,
      head: [],
      body: contratData,
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50, textColor: [100, 100, 100] },
        1: { cellWidth: 80, textColor: [30, 30, 30] }
      },
      margin: { left: 28 }
    });
    
    // Montant total
    const totalY = doc.lastAutoTable.finalY + 20;
    doc.setFillColor(...primaryColor);
    doc.roundedRect(pageWidth - 90, totalY, 70, 30, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.text("TOTAL TTC", pageWidth - 55, totalY + 10, { align: "center" });
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(`${getCurrentPrice()} €`, pageWidth - 55, totalY + 23, { align: "center" });
    
    // Pied de page
    const footerY = 270;
    doc.setDrawColor(200, 200, 200);
    doc.line(20, footerY, pageWidth - 20, footerY);
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("JYVIS Assurances - SARL au capital de 10 000€ - SIRET: 123 456 789 00012", pageWidth / 2, footerY + 8, { align: "center" });
    doc.text("Courtier en assurances - ORIAS n° 12 345 678 - RC Professionnelle conforme", pageWidth / 2, footerY + 14, { align: "center" });
    doc.text("Document généré automatiquement - Conservez précieusement cette facture", pageWidth / 2, footerY + 20, { align: "center" });
    
    // Télécharger le PDF
    doc.save(`Facture_JYVIS_${form.plaque || 'temporaire'}_${invoiceNumber}.pdf`);
    toast.success("Facture PDF téléchargée !");
  };

  // Générer mémo véhicule PDF
  const generateMemoVehicule = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const memoNumber = `MEMO-${Date.now()}`;
    const currentDate = new Date().toLocaleDateString('fr-FR');
    
    // Couleurs
    const primaryColor = [16, 38, 146]; // #102692
    const accentColor = [249, 115, 22]; // #F97316
    
    // En-tête avec fond bleu
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 45, 'F');
    
    // Logo JYVIS
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.text("JYVIS", 20, 25);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("ASSURANCES", 20, 33);
    
    // Informations société
    doc.setFontSize(9);
    doc.text("47 rue Jean Jaurès, 93200 Saint-Denis", pageWidth - 20, 25, { align: "right" });
    doc.text("Tél: " + contactInfo.phone, pageWidth - 20, 32, { align: "right" });
    
    // Titre MÉMO VÉHICULE
    doc.setTextColor(...primaryColor);
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text("MÉMO VÉHICULE", pageWidth / 2, 60, { align: "center" });
    
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text("Attestation d'assurance temporaire", pageWidth / 2, 68, { align: "center" });
    
    // Cadre principal véhicule
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(20, 80, pageWidth - 40, 50, 5, 5, 'F');
    
    // Icône véhicule (cercle avec texte)
    doc.setFillColor(...accentColor);
    doc.circle(45, 105, 15, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.text(form.vehiculeType === "auto" ? "🚗" : "🏍️", 45, 110, { align: "center" });
    
    // Infos véhicule
    doc.setTextColor(...primaryColor);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(form.plaque || "XX-XXX-XX", 70, 95);
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);
    doc.text(`${vehicleInfo?.brand || "-"} ${vehicleInfo?.model || "-"}`, 70, 105);
    doc.text(`Type: ${form.vehiculeType === "auto" ? "Automobile" : "Motocyclette"}`, 70, 115);
    doc.text(`Carte grise barrée: ${form.carteGriseBarree ? "Oui" : "Non"}`, 70, 125);
    
    // Section Conducteur
    doc.setFillColor(...primaryColor);
    doc.roundedRect(20, 140, pageWidth - 40, 45, 5, 5, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("CONDUCTEUR ASSURÉ", 30, 152);
    
    doc.setFontSize(14);
    doc.text(`${form.prenom} ${form.nom}`, 30, 165);
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Né(e) le ${form.dateNaissance ? new Date(form.dateNaissance).toLocaleDateString('fr-FR') : "-"} à ${form.lieuNaissance || "-"} (${form.paysNaissance || "France"})`, 30, 177);
    
    // Période de couverture
    doc.setFillColor(16, 185, 129); // vert
    doc.roundedRect(20, 195, pageWidth - 40, 40, 5, 5, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("PÉRIODE DE COUVERTURE", 30, 207);
    
    const dateDebut = form.dateDebutContrat ? new Date(form.dateDebutContrat).toLocaleDateString('fr-FR') : "-";
    const dateFin = form.dateFinContrat || calculateEndDate(form.dateDebutContrat, form.duree);
    const dateFinFormatted = dateFin ? new Date(dateFin).toLocaleDateString('fr-FR') : "-";
    
    doc.setFontSize(16);
    doc.text(`Du ${dateDebut} à ${form.heureDebutContrat || "00:00"}`, 30, 220);
    doc.text(`Au ${dateFinFormatted} à ${form.heureDebutContrat || "00:00"}`, 30, 230);
    
    // Durée badge
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(pageWidth - 60, 205, 35, 25, 3, 3, 'F');
    doc.setTextColor(...primaryColor);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(`${form.duree}`, pageWidth - 42, 217, { align: "center" });
    doc.setFontSize(10);
    doc.text("mois", pageWidth - 42, 226, { align: "center" });
    
    // QR code placeholder (carré avec texte)
    doc.setFillColor(240, 240, 240);
    doc.roundedRect(pageWidth / 2 - 25, 245, 50, 50, 3, 3, 'F');
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(8);
    doc.text("Scan pour", pageWidth / 2, 265, { align: "center" });
    doc.text("vérification", pageWidth / 2, 272, { align: "center" });
    doc.text(`Réf: ${memoNumber}`, pageWidth / 2, 285, { align: "center" });
    
    // Pied de page
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(8);
    doc.text("Ce document atteste de la souscription d'une assurance temporaire. À présenter en cas de contrôle.", pageWidth / 2, 305, { align: "center" });
    doc.text(`Généré le ${currentDate} - JYVIS Assurances - ORIAS n° 12 345 678`, pageWidth / 2, 312, { align: "center" });
    
    // Télécharger
    doc.save(`Memo_Vehicule_JYVIS_${form.plaque || 'temporaire'}_${memoNumber}.pdf`);
    toast.success("Mémo véhicule PDF téléchargé !");
  };

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Page de confirmation avec bouton de paiement
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center"
        >
          {paymentConfirmed ? (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-3">Merci, paiement signalé !</h1>
              <p className="text-gray-600 mb-4">
                Merci <strong>{form.prenom}</strong> ! Dès que votre paiement est vérifié, votre attestation d'assurance temporaire est envoyée 
                par email à <strong>{form.email}</strong>.
              </p>
              <div className="bg-green-50 rounded-lg p-4 mb-6 border border-green-200">
                <p className="text-sm text-green-700">Montant</p>
                <p className="text-3xl font-bold text-green-600">{getCurrentPrice()} €</p>
                <p className="text-sm text-green-600 mt-1">Durée : {form.duree} mois</p>
              </div>
              <div className="flex flex-col gap-3">
                <Button onClick={generateInvoice} className="flex items-center gap-2 bg-[#102692] hover:bg-[#0D1F75] text-white">
                  <Download className="w-4 h-4" />
                  Télécharger la facture PDF
                </Button>
                <Button onClick={generateMemoVehicule} variant="outline" className="flex items-center gap-2 border-[#102692] text-[#102692] hover:bg-[#102692]/10">
                  <FileText className="w-4 h-4" />
                  Télécharger le mémo véhicule PDF
                </Button>
                <Button onClick={() => navigate("/")} variant="ghost" className="mt-2">
                  Retour à l'accueil
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CreditCard className="w-10 h-10 text-[#102692]" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-3">Finalisez votre paiement</h1>
              <p className="text-gray-600 mb-4">
                Cliquez sur le bouton ci-dessous pour procéder au paiement sécurisé via Stripe.
              </p>
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-600">Montant à régler</p>
                <p className="text-3xl font-bold text-[#102692]">{getCurrentPrice()} €</p>
                <p className="text-sm text-gray-500">Assurance {form.vehiculeType === "auto" ? "Auto" : "Moto"} - {form.duree} mois</p>
              </div>
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => startCheckout(subscriptionId)}
                  disabled={!subscriptionId}
                  className="w-full h-14 bg-[#102692] hover:bg-[#0D1F75] text-white font-semibold text-lg rounded-xl shadow-lg transition-all flex items-center justify-center gap-3"
                  data-testid="temp-pay-btn"
                >
                  <CreditCard className="w-5 h-5" />
                  Payer {getCurrentPrice()} € par carte
                </Button>
                <div className="text-center text-sm text-gray-500 my-2">
                  Vous avez déjà payé autrement ? Signalez-le ci-dessous
                </div>
                <Button 
                  onClick={confirmPayment}
                  variant="outline"
                  className="w-full h-12 border-green-600 text-green-700 font-semibold rounded-xl"
                  data-testid="temp-declare-paid-btn"
                >
                  <Check className="w-5 h-5 mr-2" />
                  J'ai payé - Confirmer
                </Button>
                <Button onClick={() => setSubmitted(false)} variant="outline" className="mt-2">
                  Modifier mes informations
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-4">
                Paiement sécurisé par Stripe. Attestation envoyée par email après confirmation.
              </p>
            </>
          )}
          <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="text-[#102692] hover:underline flex items-center justify-center gap-2 mt-4">
            <Phone className="w-4 h-4" />
            {contactInfo.phone}
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#102692] text-white py-4 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <HeaderLogo theme="contact" size="sm" showName={true} dark={true} />
          </Link>
          <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition-colors">
            <Phone className="w-4 h-4" />
            <span className="hidden md:inline">{contactInfo.phone}</span>
          </a>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-4 md:p-6">
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          {/* Entête */}
          <div className="bg-gradient-to-r from-[#F97316] to-[#EA580C] text-white p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Zap className="w-6 h-6" />
              <span className="text-sm font-medium uppercase tracking-wide">Rapide & Simple</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center justify-center gap-3">
              <Clock className="w-8 h-8" />
              Assurance Temporaire
            </h1>
            <p className="text-orange-100 mt-2">Auto & Moto - Attestation immédiate</p>
          </div>

          {/* Sélection type et durée */}
          <div className="p-6 bg-gray-50 border-b">
            <div className="flex flex-col md:flex-row gap-6 justify-center items-center">
              {/* Type de véhicule */}
              <div className="flex gap-3">
                <button
                  onClick={() => updateForm("vehiculeType", "auto")}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl border-2 transition-all ${
                    form.vehiculeType === "auto" 
                      ? "border-[#F97316] bg-orange-50 text-[#F97316]" 
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <Car className="w-5 h-5" />
                  <span className="font-semibold">Auto</span>
                </button>
                <button
                  onClick={() => updateForm("vehiculeType", "moto")}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl border-2 transition-all ${
                    form.vehiculeType === "moto" 
                      ? "border-[#F97316] bg-orange-50 text-[#F97316]" 
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <Bike className="w-5 h-5" />
                  <span className="font-semibold">Moto</span>
                </button>
              </div>

              {/* Durée */}
              <div className="flex gap-3">
                <button
                  onClick={() => updateForm("duree", "1")}
                  className={`flex flex-col items-center px-6 py-3 rounded-xl border-2 transition-all ${
                    form.duree === "1" 
                      ? "border-[#F97316] bg-orange-50" 
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <span className="font-bold text-lg">{TARIFS[form.vehiculeType][1].prix} €</span>
                  <span className="text-sm text-gray-500">1 mois</span>
                </button>
                <button
                  onClick={() => updateForm("duree", "2")}
                  className={`flex flex-col items-center px-6 py-3 rounded-xl border-2 transition-all ${
                    form.duree === "2" 
                      ? "border-[#F97316] bg-orange-50" 
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <span className="font-bold text-lg">{TARIFS[form.vehiculeType][2].prix} €</span>
                  <span className="text-sm text-gray-500">2 mois</span>
                </button>
              </div>
            </div>
          </div>

          {/* Formulaire */}
          <div className="p-6 md:p-8 space-y-6">
            
            {/* Plaque */}
            <div className="bg-gray-50 rounded-xl p-5">
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-3">
                <Search className="w-4 h-4 text-[#F97316]" />
                Plaque d'immatriculation *
              </Label>
              <div className="flex gap-2">
                <Input
                  value={form.plaque}
                  onChange={(e) => updateForm("plaque", e.target.value.toUpperCase())}
                  placeholder="AB-123-CD"
                  className="uppercase text-lg h-12 flex-1 text-center font-mono"
                  maxLength={9}
                />
                <Button 
                  onClick={lookupPlate} 
                  disabled={loading}
                  className="h-12 px-6 bg-[#F97316] hover:bg-[#EA580C]"
                >
                  {loading ? "..." : "Rechercher"}
                </Button>
              </div>
              
              {vehicleInfo && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200"
                >
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-semibold">{vehicleInfo.brand} {vehicleInfo.model}</span>
                    <span className="text-green-600">• {vehicleInfo.year}</span>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Carte grise barrée */}
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="carteGriseBarree"
                  checked={form.carteGriseBarree}
                  onChange={(e) => updateForm("carteGriseBarree", e.target.checked)}
                  className="w-5 h-5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                />
                <label htmlFor="carteGriseBarree" className="text-sm font-medium text-amber-800 cursor-pointer">
                  La carte grise est barrée (véhicule vendu ou en cours de cession)
                </label>
              </div>
            </div>

            {/* Informations personnelles */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <User className="w-5 h-5 text-[#102692]" />
                Informations du conducteur
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Prénom *</Label>
                  <Input
                    value={form.prenom}
                    onChange={(e) => updateForm("prenom", e.target.value)}
                    className="mt-1 h-11"
                    placeholder="Votre prénom"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Nom *</Label>
                  <Input
                    value={form.nom}
                    onChange={(e) => updateForm("nom", e.target.value)}
                    className="mt-1 h-11"
                    placeholder="Votre nom"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Date de naissance *</Label>
                  <Input
                    type="date"
                    value={form.dateNaissance}
                    onChange={(e) => updateForm("dateNaissance", e.target.value)}
                    className="mt-1 h-11"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Lieu de naissance *</Label>
                  <Input
                    value={form.lieuNaissance}
                    onChange={(e) => updateForm("lieuNaissance", e.target.value)}
                    className="mt-1 h-11"
                    placeholder="Paris, Lyon, etc."
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Département de naissance</Label>
                  <Input
                    value={form.departementNaissance}
                    onChange={(e) => updateForm("departementNaissance", e.target.value)}
                    className="mt-1 h-11"
                    placeholder="75, 93, 971, etc."
                    maxLength={3}
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Pays de naissance *</Label>
                  <Input
                    value={form.paysNaissance}
                    onChange={(e) => updateForm("paysNaissance", e.target.value)}
                    className="mt-1 h-11"
                    placeholder="France"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Téléphone *</Label>
                  <Input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={form.telephone}
                    onChange={(e) => updateForm("telephone", e.target.value)}
                    className="mt-1 h-11"
                    placeholder="06 12 34 56 78"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Email *</Label>
                  <Input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => updateForm("email", e.target.value)}
                    className="mt-1 h-11"
                    placeholder="votre@email.com"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-sm font-medium text-gray-700">Adresse</Label>
                  <Input
                    value={form.adresse}
                    onChange={(e) => updateForm("adresse", e.target.value)}
                    className="mt-1 h-11"
                    placeholder="123 rue Example"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Code postal</Label>
                  <Input
                    value={form.codePostal}
                    onChange={(e) => updateForm("codePostal", e.target.value)}
                    className="mt-1 h-11"
                    placeholder="93200"
                    maxLength={5}
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Ville</Label>
                  <Input
                    value={form.ville}
                    onChange={(e) => updateForm("ville", e.target.value)}
                    className="mt-1 h-11"
                    placeholder="Saint-Denis"
                  />
                </div>
              </div>
            </div>

            {/* Permis de conduire */}
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-blue-600" />
                Permis de conduire
              </h3>
              <div>
                <Label className="text-sm font-medium text-gray-700">Date d'obtention du permis *</Label>
                <Input
                  type="date"
                  value={form.datePermis}
                  onChange={(e) => updateForm("datePermis", e.target.value)}
                  className="mt-1 h-11 max-w-xs"
                />
                <p className="text-xs text-gray-500 mt-1">Date à laquelle vous avez obtenu votre permis de conduire</p>
              </div>
            </div>

            {/* Dates du contrat */}
            <div className="bg-green-50 rounded-xl p-5 border border-green-200">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-green-600" />
                Dates du contrat
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Date de début *</Label>
                  <Input
                    type="date"
                    value={form.dateDebutContrat}
                    onChange={(e) => {
                      updateForm("dateDebutContrat", e.target.value);
                      updateContractDates(e.target.value, form.duree);
                    }}
                    min={new Date().toISOString().split('T')[0]}
                    className="mt-1 h-11"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Heure de début *</Label>
                  <Input
                    type="time"
                    value={form.heureDebutContrat}
                    onChange={(e) => updateForm("heureDebutContrat", e.target.value)}
                    className="mt-1 h-11"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Date de fin (calculée)</Label>
                  <Input
                    type="date"
                    value={form.dateFinContrat || calculateEndDate(form.dateDebutContrat, form.duree)}
                    readOnly
                    className="mt-1 h-11 bg-gray-100"
                  />
                </div>
              </div>
              {form.dateDebutContrat && (
                <div className="mt-3 p-3 bg-white rounded-lg border border-green-200">
                  <p className="text-sm text-green-700">
                    <strong>Période couverte :</strong> du {new Date(form.dateDebutContrat).toLocaleDateString('fr-FR')} 
                    {form.heureDebutContrat && ` à ${form.heureDebutContrat}`} au {new Date(calculateEndDate(form.dateDebutContrat, form.duree)).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}
            </div>

            {/* Documents OBLIGATOIRES */}
            <div className="bg-orange-50 rounded-xl p-5 border border-orange-200">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#F97316]" />
                Documents obligatoires
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Carte grise */}
                <div>
                  <label className={`flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                    carteGrise ? "border-green-400 bg-green-50" : "border-gray-300 bg-white hover:bg-gray-50"
                  }`} data-testid="upload-carte-grise">
                    <CreditCard className={`w-8 h-8 mb-2 ${carteGrise ? "text-green-500" : "text-gray-400"}`} />
                    <span className="text-sm text-gray-600 font-medium text-center">Carte grise</span>
                    <span className="text-xs text-gray-400">(PDF/Photo)</span>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setCarteGrise(e.target.files[0])}
                    />
                  </label>
                  {carteGrise && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1 justify-center">
                      <Check className="w-3 h-3" /> OK
                    </p>
                  )}
                </div>
                
                {/* Permis de conduire */}
                <div>
                  <label className={`flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                    permisConduire ? "border-green-400 bg-green-50" : "border-gray-300 bg-white hover:bg-gray-50"
                  }`} data-testid="upload-permis">
                    <IdCard className={`w-8 h-8 mb-2 ${permisConduire ? "text-green-500" : "text-gray-400"}`} />
                    <span className="text-sm text-gray-600 font-medium text-center">Permis de conduire</span>
                    <span className="text-xs text-gray-400">(PDF/Photo)</span>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setPermisConduire(e.target.files[0])}
                    />
                  </label>
                  {permisConduire && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1 justify-center">
                      <Check className="w-3 h-3" /> OK
                    </p>
                  )}
                </div>
                
                {/* Photo du véhicule */}
                <div>
                  <label className={`flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                    photoVehicule ? "border-green-400 bg-green-50" : "border-gray-300 bg-white hover:bg-gray-50"
                  }`} data-testid="upload-photo">
                    <Camera className={`w-8 h-8 mb-2 ${photoVehicule ? "text-green-500" : "text-gray-400"}`} />
                    <span className="text-sm text-gray-600 font-medium text-center">Photo véhicule</span>
                    <span className="text-xs text-gray-400">(Photo)</span>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept=".jpg,.jpeg,.png"
                      onChange={(e) => setPhotoVehicule(e.target.files[0])}
                    />
                  </label>
                  {photoVehicule && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1 justify-center">
                      <Check className="w-3 h-3" /> OK
                    </p>
                  )}
                </div>
                
                {/* Relevé d'information - Recommandé */}
                <div>
                  <label className={`flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                    releveInfo ? "border-green-400 bg-green-50" : "border-[#F97316]/50 bg-orange-50/50 hover:bg-orange-100"
                  }`} data-testid="upload-releve-info">
                    <FileText className={`w-8 h-8 mb-2 ${releveInfo ? "text-green-500" : "text-[#F97316]"}`} />
                    <span className="text-sm text-gray-600 font-medium text-center">Relevé d'information</span>
                    <span className="text-xs text-[#F97316]">(Recommandé)</span>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setReleveInfo(e.target.files[0])}
                    />
                  </label>
                  {releveInfo && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1 justify-center">
                      <Check className="w-3 h-3" /> OK
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Récapitulatif */}
            <div className="bg-[#102692] text-white rounded-xl p-5">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <div>
                  <p className="text-blue-200 text-sm">Assurance temporaire {form.vehiculeType === "auto" ? "Auto" : "Moto"}</p>
                  <p className="text-lg">Durée : <strong>{form.duree} mois</strong></p>
                </div>
                <div className="text-center">
                  <p className="text-blue-200 text-sm">Total TTC</p>
                  <p className="text-4xl font-bold">{getCurrentPrice()} €</p>
                </div>
              </div>
            </div>

            {/* Conditions */}
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <Checkbox
                id="accept"
                checked={accepted}
                onCheckedChange={setAccepted}
                className="mt-1"
              />
              <label htmlFor="accept" className="text-sm text-gray-600 cursor-pointer">
                J'accepte les conditions générales et je certifie que les informations fournies sont exactes.
              </label>
            </div>

            {/* Boutons d'action */}
            <div className="space-y-3">
              {/* Bouton de paiement principal - Bleu */}
              <Button 
                onClick={handlePayment}
                disabled={loading || !accepted || !carteGrise || !permisConduire || !photoVehicule}
                className="w-full h-14 bg-[#102692] hover:bg-[#0D1F75] text-white font-semibold text-lg rounded-xl shadow-lg transition-all disabled:opacity-50"
                data-testid="payment-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Traitement en cours...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Payer {getCurrentPrice()} € - Assurance {form.duree} mois
                  </span>
                )}
              </Button>
              
              {/* Séparateur */}
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-gray-200"></div>
                <span className="text-gray-400 text-sm">ou</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>
              
              {/* Bouton WhatsApp - Rappel immédiat */}
              <a 
                href={contactInfo.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full h-12 bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold rounded-xl shadow-lg transition-all flex items-center justify-center gap-3"
                data-testid="whatsapp-btn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Être rappelé pour souscrire
              </a>
            </div>

            <p className="text-center text-xs text-gray-400">
              Paiement sécurisé par Stripe - Attestation envoyée par email immédiatement
            </p>
          </div>
        </motion.div>
      </div>
      <SimpleLegalFooter />
    </div>
  );
}
